import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Grid,
    Divider,
    Collapse,
    IconButton,
    Stack
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

interface TorExtractionResult {
    project_title?: string;
    client_organization?: string;
    methodology_requirements?: {
        required_methods?: string[];
        recommended_methods?: string[];
        required_frameworks?: string[];
        specific_techniques?: string[];
    };
    evaluation_questions?: string[];
    scope?: {
        geographic?: string;
        timeframe?: string;
        target_populations?: string[];
    };
    deliverables?: string[];
}

interface ExtractedRequirementsSummaryProps {
    extraction: TorExtractionResult;
}

const ExtractedRequirementsSummary: React.FC<ExtractedRequirementsSummaryProps> = ({ extraction }) => {
    const [questionsExpanded, setQuestionsExpanded] = useState(false);
    
    // Safe extraction with null checks
    const allMethods = [
        ...(extraction?.methodology_requirements?.required_methods || []),
        ...(extraction?.methodology_requirements?.recommended_methods || [])
    ];
    
    const techniques = extraction?.methodology_requirements?.specific_techniques || [];
    const frameworks = extraction?.methodology_requirements?.required_frameworks || [];
    const questions = extraction?.evaluation_questions || [];
    const deliverables = extraction?.deliverables || [];
    const targetPopulations = extraction?.scope?.target_populations || [];
    
    // Don't render if no data
    const hasData = allMethods.length > 0 || techniques.length > 0 || frameworks.length > 0 || 
                    questions.length > 0 || deliverables.length > 0 || extraction?.project_title;
    
    if (!hasData) {
        return null;
    }
    
    return (
        <Paper 
            elevation={3} 
            sx={{ 
                p: 3, 
                mb: 3,
                background: 'linear-gradient(135deg, #263238 0%, #37474f 100%)',
                border: '1px solid rgba(0, 165, 153, 0.3)'
            }}
        >
            {/* Project Overview */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>
                    PROJECT OVERVIEW
                </Typography>
                <Divider sx={{ mb: 2, borderColor: 'rgba(0, 165, 153, 0.3)' }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Title
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                            {extraction.project_title || 'N/A'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Client
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                            {extraction.client_organization || 'N/A'}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
            
            {/* Methodology Requirements */}
            <Box sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="overline" sx={{ color: '#00A599', fontWeight: 600 }}>
                        METHODOLOGY REQUIREMENTS
                    </Typography>
                </Stack>
                <Divider sx={{ mb: 2, borderColor: 'rgba(0, 165, 153, 0.3)' }} />
                
                {allMethods.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 1 }}>
                            Required Methods:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {allMethods.map((method, idx) => (
                                <Chip
                                    key={idx}
                                    label={method}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(0, 165, 153, 0.2)',
                                        color: '#00A599',
                                        border: '1px solid rgba(0, 165, 153, 0.5)',
                                        fontWeight: 500
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
                
                {techniques.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 1 }}>
                            Techniques:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {techniques.map((technique, idx) => (
                                <Chip
                                    key={idx}
                                    label={technique}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(0, 165, 153, 0.2)',
                                        color: '#00A599',
                                        border: '1px solid rgba(0, 165, 153, 0.5)',
                                        fontWeight: 500
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
                
                {frameworks.length > 0 && (
                    <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 1 }}>
                            Frameworks:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {frameworks.map((framework, idx) => (
                                <Chip
                                    key={idx}
                                    label={framework}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(255, 167, 38, 0.2)',
                                        color: '#FFA726',
                                        border: '1px solid rgba(255, 167, 38, 0.5)',
                                        fontWeight: 500
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>
            
            {/* Evaluation Questions */}
            {questions.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="overline" sx={{ color: '#00A599', fontWeight: 600 }}>
                            EVALUATION QUESTIONS ({questions.length})
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => setQuestionsExpanded(!questionsExpanded)}
                            sx={{
                                color: '#00A599',
                                transform: questionsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s'
                            }}
                        >
                            <ExpandMoreIcon />
                        </IconButton>
                    </Stack>
                    <Divider sx={{ mb: 2, borderColor: 'rgba(0, 165, 153, 0.3)' }} />
                    
                    <Collapse in={questionsExpanded}>
                        <Box component="ul" sx={{ pl: 2, color: 'rgba(255,255,255,0.9)' }}>
                            {questions.map((question, idx) => (
                                <Typography 
                                    key={idx} 
                                    component="li" 
                                    variant="body2" 
                                    sx={{ mb: 1, lineHeight: 1.6 }}
                                >
                                    {question}
                                </Typography>
                            ))}
                        </Box>
                    </Collapse>
                    
                    {!questionsExpanded && questions.length > 0 && (
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                            {questions[0]}
                            {questions.length > 1 && (
                                <Typography 
                                    component="span" 
                                    variant="caption" 
                                    sx={{ color: '#00A599', ml: 1, cursor: 'pointer' }}
                                    onClick={() => setQuestionsExpanded(true)}
                                >
                                    +{questions.length - 1} more questions
                                </Typography>
                            )}
                        </Typography>
                    )}
                </Box>
            )}
            
            {/* Scope & Coverage */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="overline" sx={{ color: '#00BCD4', fontWeight: 600 }}>
                    SCOPE & COVERAGE
                </Typography>
                <Divider sx={{ mb: 2, borderColor: 'rgba(0, 165, 153, 0.3)' }} />
                
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Geographic:
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                            {extraction.scope?.geographic || 'Not specified'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Timeframe:
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                            {extraction.scope?.timeframe || 'Not specified'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Target Populations:
                        </Typography>
                        {targetPopulations.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {targetPopulations.map((pop, idx) => (
                                    <Chip
                                        key={idx}
                                        label={pop}
                                        size="small"
                                        sx={{
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            color: 'white',
                                            fontSize: '0.7rem',
                                            border: '1px solid rgba(255,255,255,0.2)'
                                        }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" sx={{ color: 'white' }}>
                                Not specified
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </Box>
            
            {/* Deliverables */}
            {deliverables.length > 0 && (
                <Box>
                    <Typography variant="overline" sx={{ color: '#00A599', fontWeight: 600 }}>
                        DELIVERABLES ({deliverables.length})
                    </Typography>
                    <Divider sx={{ mb: 2, borderColor: 'rgba(0, 165, 153, 0.3)' }} />
                    
                    <Box component="ul" sx={{ pl: 2, color: 'rgba(255,255,255,0.9)', m: 0 }}>
                        {deliverables.map((deliverable, idx) => (
                            <Typography 
                                key={idx} 
                                component="li" 
                                variant="body2" 
                                sx={{ mb: 0.5 }}
                            >
                                {deliverable}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}
        </Paper>
    );
};

export default ExtractedRequirementsSummary;
